import { useState, useEffect } from 'react';

const matchesNow = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

/**
 * Whether a CSS media query matches, kept up to date through the matchMedia change listener.
 * Seeded from the current match so the first render already has the right layout.
 *
 * @example
 * const isWide = useMediaQuery('(min-width: 1280px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => matchesNow(query));

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    setMatches(mediaQueryList.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQueryList.addEventListener('change', handleChange);

    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}

/** Phone width: below Tailwind's `md` (768px), where tables become cards and actions icon-only. */
export const MOBILE_QUERY = '(max-width: 767px)';

/** Below Tailwind's `lg` (1024px), where the sidebar is a drawer opened from the top bar. */
export const BELOW_DESKTOP_QUERY = '(max-width: 1023px)';

/** `true` on a phone-width screen (below 768px, the same cut-off as Tailwind's `max-md:`). */
export const useIsMobile = (): boolean => useMediaQuery(MOBILE_QUERY);

/** `true` below 1024px (Tailwind's `max-lg:`), where navigation is a drawer. */
export const useIsBelowDesktop = (): boolean => useMediaQuery(BELOW_DESKTOP_QUERY);
